import * as turf from '@turf/turf'
import tokml from 'geojson-to-kml'
import { ruutsApi } from '.'
import { ELIGIBILITY_QUERY } from '../../hooks/data/useEligibilityCheck'
import { exclusionAreaTypesQueryOptions } from '../../hooks/data/useExclusionAreaTypes'
import { queryClient } from '../../queryClient'
import { isEligibilityEnabled } from '../../utils/programConfig/areaSubtractions'
import { GeoJSON } from '../../utils/GeoJSON/index'
import getFarmSubdivisionById from '../farmMapping/getFarmSubdivisionById'
import getMonitoringSites from '../farmMapping/monitoringSites/getMonitoringSites'
import { getMonitoringSoc } from '../farmMapping/getMonitoringSoc'
import { errors } from './errors'
import { convertToCSV } from '../../utils/convertToCSV'
import { getFarmProjectArea } from '../farmMapping/getFarms'
import getEligibilityCheck from './getEligibilityCheck'

const filterPrivateInformation = feature => {
  const { properties } = feature
  const {
    name,
    otherName,
    label,
    color,
    area,
    exclusionAreaTypeId,
    exclusionAreaTypeDescription,
    type,
    lat,
    lon,
    hasGrazingManagement,
    is_random_site,
    monitoring_site_id,
    monitoring_site_name,
    monitoring_task_id,
    monitoring_task_key,
    sampling_area_id,
    farm_id,
    station_id,
    stratum_id,
    farm_name,
    farm_code,
  } = properties

  const propertiesToKeep = {
    type,
    name,
    otherName,
    label,
    area,
    color,
    exclusionAreaTypeId,
    exclusionAreaTypeDescription,
    hasGrazingManagement,
    lat,
    lon,
    is_random_site,
    monitoring_site_id,
    monitoring_site_name,
    monitoring_task_id,
    monitoring_task_key,
    sampling_area_id,
    farm_id,
    station_id,
    stratum_id,
    farm_name,
    farm_code,
  }

  const propertiesWithValue = Object.fromEntries(
    Object.entries(propertiesToKeep).filter(([_key, value]) => ![undefined, null].includes(value)),
  )

  // TODO: Some total hectares are not being calculated correctly
  // Recalculate area if it's present because it might be incorrect
  // This is a temporary fix until we can calculate the area correctly on the backend
  if (propertiesWithValue.area !== undefined) {
    const featureArea = GeoJSON.hectareArea(feature)
    propertiesWithValue.area = featureArea
  }

  return { ...feature, properties: propertiesWithValue }
}

export const getMapaToDownload = async ({
  currentFarm,
  subdivisionId,
  includeSoilInfo,
  includeProjectArea,
  programConfig,
}) => {
  const perimeter = currentFarm.toGeoJSON

  const loadPaddocks = async () => {
    if (!subdivisionId) return []
    const subdivision = await getFarmSubdivisionById({
      id: subdivisionId,
      farmId: currentFarm.id,
    })
    return Promise.all(
      (subdivision?.paddockIds ?? []).map(async paddockId => {
        const result = await ruutsApi.paddocks.getPaddockById({ id: paddockId })
        if (result.length !== 1)
          throw new errors.BusinessEntityError({
            message: `Error de inconsistencia al descargar el paddock id ${paddockId}`,
          })
        return result[0].toGeoJSON
      }),
    )
  }

  const loadSamplingAreas = async () => {
    const rawSamplingAreas = await ruutsApi.samplingAreas.getUncroppedByFarm({
      farmId: currentFarm.id,
    })
    return rawSamplingAreas.map(samplingArea => samplingArea?.toGeoJSON)
  }

  // name is the category and otherName the user label — both are exported because the importer
  // rebuilds the pair from them (otherName ?? name). Dropping otherName leaves the imported area
  // nameless and blocks the save behind "Debe asignar un nombre a todas las áreas".
  const loadExclusionAreas = async () => {
    const [exclusionTypes, rawExclusionAreas] = await Promise.all([
      queryClient.ensureQueryData(exclusionAreaTypesQueryOptions(currentFarm.programId)),
      ruutsApi.exclusionAreas.getExclusionAreasByFarm({ farmId: currentFarm.id }),
    ])
    return rawExclusionAreas.map(feature => {
      const exclusionAreaTypeDescription = exclusionTypes.find(
        exclusionType => exclusionType.id === feature.properties.exclusionAreaTypeId,
      )
      const properties = { ...feature.properties }
      if (exclusionAreaTypeDescription) {
        properties.exclusionAreaTypeDescription = exclusionAreaTypeDescription.en_US
      }
      return { ...feature, properties }
    })
  }

  // The catch lives inside the thunk so an eligibility-service outage degrades to an empty layer
  // instead of rejecting the Promise.all and taking the whole download down. name is forced to
  // label (not defaulted) because composed layers like forestAndRed carry a truthy, non-display
  // backend name (e.g. "<farmName>-forestAndRed"); label is the canonical display field used by
  // every visualization surface, and tokml reads placemark names from properties.name.
  const loadEligibilityAreas = async () => {
    if (!isEligibilityEnabled(programConfig)) return []
    try {
      const rawEligibilityAreas = await getEligibilityCheck({
        farmId: currentFarm.id,
        query: ELIGIBILITY_QUERY,
      })
      return rawEligibilityAreas.map(feature => ({
        ...feature,
        properties: {
          ...feature.properties,
          name: feature.properties.label,
        },
      }))
    } catch (error) {
      console.error(error)
      return []
    }
  }

  const loadMonitoring = async () => {
    if (includeSoilInfo) {
      const { monitoringSocSites, monitoringSites: allMonitoringSites } = await getMonitoringSoc({
        farmId: currentFarm.id,
      })
      return { monitoringSites: monitoringSocSites, extraInformation: allMonitoringSites }
    }
    const rawMonitoringSites = await getMonitoringSites({ farmId: currentFarm.id })
    return {
      monitoringSites: rawMonitoringSites.map(monitoringSite => monitoringSite?.toGeoJSON),
      extraInformation: null,
    }
  }

  const loadProjectArea = async () =>
    includeProjectArea ? getFarmProjectArea({ farmId: currentFarm.id }) : null

  const [paddocks, samplingAreas, exclusionAreas, eligibilityAreas, monitoring, projectArea] =
    await Promise.all([
      loadPaddocks(),
      loadSamplingAreas(),
      loadExclusionAreas(),
      loadEligibilityAreas(),
      loadMonitoring(),
      loadProjectArea(),
    ])

  const { monitoringSites, extraInformation } = monitoring

  const features = [
    perimeter,
    ...paddocks,
    ...samplingAreas,
    ...exclusionAreas,
    ...eligibilityAreas,
    ...monitoringSites,
  ]
  if (projectArea) features.push(projectArea)

  const geojson = turf.featureCollection(features.map(feature => filterPrivateInformation(feature)))

  const kml = tokml(geojson)

  const map = {
    geojson: JSON.stringify(geojson),
    kml,
    csv: extraInformation && convertToCSV(extraInformation),
  }

  return map
}
